import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Chip,
  Grid as Grid2,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Check, X } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { useEditableImagesContext } from '@modules/_shared/components/image-gallery/context/editableImagesContext';
import { ImageEditPanel } from '@modules/_shared/components/image-gallery/ImageEditPanel';
import {
  type ImageGalleryItem,
  NewImageGallery,
  type NewImageGalleryRef,
} from '@modules/_shared/components/image-gallery/NewImageGallery';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useCompositeWatermarkImages } from '@modules/_shared/components/watermark/useCompositeWatermarkImages';
import { GET_LOT_WATERMARK_CONFIG } from '@modules/_shared/components/watermark/watermarkQueries';
import { WatermarkSettingsDialog } from '@modules/_shared/components/watermark/WatermarkSettingsDialog';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';

type EditableImagesProps = {
  isOnePanelDisplay?: boolean;
  canOpenRightPanel?: boolean;
  disableNavigation?: boolean;
  disableUpload?: boolean;
  ImageForm: React.FC<{ imageId: string }>;
  showCaptions?: boolean;
  onShowCaptionsChange?: (show: boolean) => void;
  lotId?: string | null;
  leftColumnHeader?: React.ReactNode;
};

export const EditableImages = memo(
  ({
    isOnePanelDisplay,
    canOpenRightPanel,
    disableNavigation,
    disableUpload,
    ImageForm,
    showCaptions,
    onShowCaptionsChange,
    lotId,
    leftColumnHeader,
  }: EditableImagesProps) => {
    const {
      readOnly,
      autoSelectFirst,
      canEditMeta,
      metaColumns,
      enableViewMode,
      images,
      deleteImage,
      reorderImage,
      loading,
      uploadImages,
      viewMode,
      setViewMode,
      snackbarMessage,
      hideSnackbar,
      regenerateAllMetadata,
      regeneratingAll,
    } = useEditableImagesContext<ImageGalleryItem>();
    const navigate = useNavigateWithSearchParams();
    const { imgId: selectedImageId } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const hasAutoSelected = useRef(false);
    const galleryRef = useRef<NewImageGalleryRef>(null);
    const [watermarkOpen, setWatermarkOpen] = useState(false);
    const {
      compositeImages,
      error: compositeError,
      reset: resetComposite,
      snackbar: skippedWarningSnackbar,
    } = useCompositeWatermarkImages();

    const { data: watermarkData } = useQuery(GET_LOT_WATERMARK_CONFIG, {
      variables: { lotId: lotId ?? '' },
      skip: !lotId,
    });
    const watermarkEnabled =
      watermarkData?.lots_by_pk?.watermark_enabled ?? false;

    const handleUploadWithWatermark = useCallback(
      async (images: { id: string; url: string }[]) => {
        const result = await uploadImages(images);
        if (watermarkEnabled && lotId) {
          compositeImages(lotId).catch(() => {});
        }
        return result;
      },
      [uploadImages, watermarkEnabled, lotId, compositeImages],
    );

    const handleOpenWatermark = useCallback(() => setWatermarkOpen(true), []);
    const handleCloseWatermark = useCallback(() => setWatermarkOpen(false), []);

    const galleryImages = images.map(img => ({
      canDelete: !readOnly,
      ...img,
    }));

    const extraColumns = canEditMeta ? metaColumns : [];

    useEffect(() => {
      if (
        !hasAutoSelected.current &&
        autoSelectFirst &&
        images.length > 0 &&
        !selectedImageId &&
        !isMobile
      ) {
        navigate(`./${images[0].id}`, { replace: true });
        hasAutoSelected.current = true;
      }
    }, [autoSelectFirst, images, selectedImageId, navigate, isMobile]);

    return (
      <>
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              width: '100%',
              zIndex: 2000,
            }}
          />
        )}
        <Grid2 container sx={{ height: '100%' }}>
          {(!selectedImageId || !isOnePanelDisplay) && (
            <Grid2
              size={{
                xs: 12,
                lg: canOpenRightPanel && !isOnePanelDisplay ? 5 : 12,
              }}
              sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                height: '100%',
                borderRight:
                  canOpenRightPanel && !isOnePanelDisplay
                    ? `1px solid ${theme.palette.divider}`
                    : 0,
                position: 'relative',
              })}
            >
              {leftColumnHeader}
              <NewImageGallery
                ref={galleryRef}
                images={galleryImages}
                onDeleteImage={readOnly ? undefined : deleteImage}
                onReorderImage={readOnly ? undefined : reorderImage}
                canEditMeta={canEditMeta}
                onViewModeChange={setViewMode}
                viewMode={viewMode}
                enableViewMode={enableViewMode}
                disableRowSelection={!canOpenRightPanel}
                extraMetaColumns={extraColumns}
                showTitleAndDescription
                showCaptions={showCaptions}
                onShowCaptionsChange={onShowCaptionsChange}
                onRegenerateAllMetadata={
                  readOnly ? undefined : regenerateAllMetadata
                }
                regeneratingAll={regeneratingAll}
                onOpenWatermarkSettings={
                  !readOnly && lotId ? handleOpenWatermark : undefined
                }
                watermarkEnabled={watermarkEnabled}
                onSelectImage={
                  disableNavigation
                    ? undefined
                    : image => navigate(`./${image.id}`, { replace: true })
                }
                onFilesUploaded={
                  readOnly || disableUpload
                    ? undefined
                    : handleUploadWithWatermark
                }
                renderCustomContent={image => (
                  <Stack direction="row" spacing={0.5}>
                    {extraColumns.map(column => {
                      const active = Boolean(image[column.field]);
                      return (
                        <Chip
                          key={column.field}
                          icon={active ? <Check size={13} /> : <X size={13} />}
                          label={column.headerName}
                          size="small"
                          sx={{
                            height: 22,
                            borderRadius: '6px',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            bgcolor: 'rgba(255, 255, 255, 0.92)',
                            backdropFilter: 'blur(4px)',
                            color: active
                              ? 'rgba(0, 0, 0, 0.87)'
                              : 'rgba(0, 0, 0, 0.38)',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                            '& .MuiChip-icon': {
                              color: active
                                ? 'success.main'
                                : 'rgba(0, 0, 0, 0.38)',
                              ml: '5px',
                              mr: '-3px',
                            },
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}
              />
            </Grid2>
          )}
          {canOpenRightPanel && (
            <Grid2
              size={{
                xs: 12,
                lg: isOnePanelDisplay ? 12 : 7,
              }}
              sx={{ position: 'relative' }}
            >
              <ImageEditPanel
                selectedImageId={selectedImageId}
                ImageForm={ImageForm}
                onFullScreenClick={index =>
                  galleryRef.current?.openFullscreen(index)
                }
              />
            </Grid2>
          )}
        </Grid2>

        {snackbarMessage && (
          <RASnackbar onClose={hideSnackbar} message={snackbarMessage} />
        )}

        <MutationErrorSnackbar
          error={compositeError ?? null}
          onClose={resetComposite}
        />

        {skippedWarningSnackbar}

        {lotId && (
          <WatermarkSettingsDialog
            open={watermarkOpen}
            onClose={handleCloseWatermark}
            lotId={lotId}
            previewImageUrl={images[0]?.image?.url}
          />
        )}
      </>
    );
  },
);
